import React, { Component, createRef, Fragment } from "react";
import { isEqual } from "lodash";
import "component/list/TableList/styles.scss";
import { withTranslation } from "react-i18next";

class TableList extends Component {
  constructor(props) {
    super(props);
    this.endRef = createRef();
    this.containerRef = createRef();
    this.observer = null;

    this.state = {
      games: null,
      showMore: false,
      headers: null,
      totalColumn: 5,
    };
  }

  initObserver() {
    if (this.observer) return;

    if (
      "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window &&
      "intersectionRatio" in window.IntersectionObserverEntry.prototype
    ) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.endRef.current && this.props.onReachEnd) {
                this.props.onReachEnd();
              }
            }
          });
        },
        {
          rootMargin: "0px 0px 0px 0px",
          threshold: 0.1,
        },
      );
      if (this.endRef.current) {
        this.observer.observe(this.endRef.current);
      }
    }
  }

  componentDidMount() {
    this.initObserver();
    if (this.props.totalColumn) {
      this.setState({
        totalColumn: this.props.totalColumn,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.observer && this.endRef.current) {
      this.initObserver();
    }

    if (!isEqual(prevProps.list, this.props.list) && this.props.list) {
      const items = this.props.list ? this.props.list.get("items") : null;
      let totalColumn = this.state.totalColumn;

      try {
        if (items && items.size > 0) {
          totalColumn = items.get(0).size;
        }
      } catch (err) {}

      const headers =
        items && items.toJS().length > 0
          ? Object.keys(items.toJS()[0])
          : null;

      this.setState({
        headers,
        totalColumn,
      });
    }

    if (
      !isEqual(prevProps.selectedItem, this.props.selectedItem) &&
      this.props.selectedItem
    ) {
      setTimeout(() => {
        if (this.containerRef.current) {
          const expandedRow = this.containerRef.current.querySelector(
            ".expand",
          );
          const scrollableParent =
            this.containerRef.current.closest(".round-history-per-game") ||
            this.containerRef.current.parentElement;

          if (expandedRow && scrollableParent) {
            const rowRect = expandedRow.getBoundingClientRect();
            const parentRect = scrollableParent.getBoundingClientRect();
            const currentScrollTop = scrollableParent.scrollTop;

            const relativeRowTop =
              rowRect.top - parentRect.top + currentScrollTop;
            const targetScrollTop =
              relativeRowTop -
              scrollableParent.clientHeight / 2 +
              expandedRow.clientHeight / 2;

            scrollableParent.scrollTo({
              top: targetScrollTop,
              behavior: "smooth",
            });
          }
        }
      }, 150);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  render() {
    const { t } = this.props;
    const items = this.props.list ? this.props.list.get("items") : null;

    if (this.props.listComplete && !items) {
      return <div className="text-center">{t("thereIsNoData")}</div>;
    }

    return (
      <div className="table-list-component" ref={this.containerRef}>
        <table className="table-list">
          <thead>
            {!this.state.headers && !this.props.listComplete && (
              <tr>
                {[...Array(this.state.totalColumn)].map((item, key) => {
                  if (this.props.setHeaderSkeleton) {
                    return this.props.setHeaderSkeleton(key);
                  }

                  return (
                    <th key={key}>
                      <span className="dummy">loading</span>
                    </th>
                  );
                })}
              </tr>
            )}
            {this.state.headers && (
              <tr>
                {this.state.headers.map((header, key) => (
                  <th
                    key={key}
                    data-testid={
                      this.props.columnTestIds &&
                      this.props.columnTestIds[header]
                        ? `${this.props.columnTestIds[header]}-column-header`
                        : undefined
                    }
                    onClick={() => {
                      if (this.props.onSort) {
                        this.props.onSort(header);
                      }
                    }}
                  >
                    {!this.props.setLabel && <span>{header}</span>}
                    {this.props.setLabel && (
                      <span>{this.props.setLabel(header)}</span>
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {items &&
              items.map((item, key) => {
                const keys = Object.keys(items.toJS()[key]);
                const values = Object.values(items.toJS()[key]);
                const isSelected = isEqual(this.props.selectedItem, item);

                return (
                  <Fragment key={key}>
                    <tr
                      className={`${this.props.onRowClick ? "enabled" : ""} ${
                        isSelected ? "selected" : ""
                      }`}
                      data-testid={this.props.rowTestId}
                      onClick={(event) => {
                        if (this.props.onRowClick) this.props.onRowClick(item);
                      }}
                    >
                      {values.map((value, key) => {
                        return (
                          <td
                            key={key}
                            data-testid={
                              this.props.columnTestIds &&
                              this.props.columnTestIds[keys[key]]
                                ? `${this.props.columnTestIds[keys[key]]}-value`
                                : undefined
                            }
                          >
                            {!this.props.setValue && value.toString()}
                            {this.props.setValue &&
                              this.props.setValue({
                                id: keys[key],
                                value,
                              })}
                          </td>
                        );
                      })}
                    </tr>
                    {isSelected && (
                      <>
                        <tr className="d-none" key={`${key}1`}>
                          <td colSpan={this.state.totalColumn} />
                        </tr>
                        <tr className="expand" key={`${key}2`}>
                          <td colSpan={this.state.totalColumn}>
                            {this.props.renderSelectedRow &&
                              this.props.renderSelectedRow()}
                          </td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                );
              })}
            <tr>
              <td
                className={`end ${this.props.initiated ? "" : "d-none"} 
                ${this.props.isLoading ? "d-none" : ""}
                ${this.props.listComplete ? "d-none" : ""}`}
                ref={this.endRef}
                colSpan={this.state.totalColumn}
              >
                end
              </td>
            </tr>
            <tr>
              <td className="d-none" colSpan={this.state.totalColumn} />
            </tr>
            {(!items || this.props.isLoading) &&
              !this.props.listComplete &&
              [...Array(10)].map((item, key) => {
                return (
                  <tr key={key}>
                    {[...Array(this.state.totalColumn)].map((subitem, key) => {
                      if (this.props.setSkeleton)
                        return this.props.setSkeleton(key);

                      return (
                        <td key={key}>
                          <span className="dummy">loading</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>

          {this.props.listComplete && !items && (
            <tfoot>
              <tr>
                <td>
                  <div className="text-center">{t("thereIsNoData")}</div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  }
}

export default withTranslation("translation")(TableList);
